import { useEffect, useState } from "react";
import { Button, Modal, Tab, Tabs, Table, Spinner } from "react-bootstrap";
import { useServerPagination } from "../../../../../hooks/customHook/paginationHook/useServerPagination";
import { PaginationControls } from "../../../../../hooks/customHook/paginationHook/usePagination";
import {
  formatToNumber,
  formatToTwoDecimal,
} from "../../../../../utils/numberFormatter";
import { ProductDeficitAccordion } from "./ProductDeficitAccordion";
import axios from "axios";
import BASE_URL from "../../../../../assets/global/url";

const DEFICIT_DETAILS_URL = `${BASE_URL}/inventoryReport/deficitProduct`;
const WAREHOUSES_URL = `${BASE_URL}/warehouse/getWarehouse`;
const MAIN_WAREHOUSE_ID = "11111111-1111-1111-1111-111111111111";

const ProductDeficitModal = ({
  show,
  onHide,
  selectedProduct,
  thisFromdate,
  thisTodate,
}) => {
  const [warehouses, setWarehouses] = useState([]);
  const [activeModule, setActiveModule] = useState("Sales Invoice");
  const [activeWarehouse, setActiveWarehouse] = useState("");
  const [tabCounts, setTabCounts] = useState([]);

  const TABLE_COL_SPAN =
    activeModule === "Sales Invoice"
      ? 6
      : activeModule === "Replenishment History"
        ? 6
        : 5;

  useEffect(() => {
    if (show && selectedProduct?.product_id) {
      Promise.all([
        axios.get(WAREHOUSES_URL),
        axios.get(
          `${DEFICIT_DETAILS_URL}/${selectedProduct.product_id}/counts`,
          { params: { startDate: thisFromdate, endDate: thisTodate } },
        ),
      ])
        .then(([whRes, countsRes]) => {
          const fetchedWarehouses = whRes.data;
          setTabCounts(countsRes.data);

          const mainWarehouse = fetchedWarehouses.find(
            (w) => w.warehouse_id === MAIN_WAREHOUSE_ID,
          );
          const otherWarehouses = fetchedWarehouses.filter(
            (w) => w.warehouse_id !== MAIN_WAREHOUSE_ID,
          );

          const allTab = { warehouse_id: "all", name: "All" };
          const sortedWarehouses = mainWarehouse
            ? [allTab, mainWarehouse, ...otherWarehouses]
            : [allTab, ...otherWarehouses];

          setWarehouses(sortedWarehouses);
          if (sortedWarehouses.length > 0) setActiveWarehouse("all");
        })
        .catch((err) =>
          console.error("Failed to fetch modal dependencies:", err),
        );
    } else {
      setActiveModule("Sales Invoice");
      setActiveWarehouse("");
      setTabCounts([]);
    }
  }, [show, selectedProduct, thisFromdate, thisTodate]);

  const isReadyToFetch = selectedProduct?.product_id && activeWarehouse;

  const apiUrl = isReadyToFetch
    ? activeModule === "Replenishment History"
      ? `${DEFICIT_DETAILS_URL}/${selectedProduct.product_id}/replenishmentHistory`
      : `${DEFICIT_DETAILS_URL}/${selectedProduct.product_id}`
    : "about:blank";

  const pagination = useServerPagination(apiUrl, 10, {
    startDate: thisFromdate,
    endDate: thisTodate,
    moduleName: activeModule,
    warehouseId: activeWarehouse,
  });

  useEffect(() => {
    if (show && isReadyToFetch) {
      pagination.updateParams({
        startDate: thisFromdate,
        endDate: thisTodate,
        moduleName: activeModule,
        warehouseId: activeWarehouse,
      });

      if (pagination.dynamicApiUrl !== apiUrl) {
        pagination.updateApiUrl(apiUrl);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeModule,
    activeWarehouse,
    thisFromdate,
    thisTodate,
    show,
    isReadyToFetch,
    apiUrl,
  ]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  };

  const getModuleCount = (moduleName) =>
    tabCounts
      .filter((c) => c.module_in_from === moduleName)
      .reduce((sum, c) => sum + Number(c.record_count), 0);

  const getWarehouseCount = (moduleName, warehouseId) => {
    const record = tabCounts.find(
      (c) => c.module_in_from === moduleName && c.warehouse_id === warehouseId,
    );
    return record ? Number(record.record_count) : 0;
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      keyboard={false}
      size="lg"
      backdrop="static"
      dialogClassName="custom-modal-vw90"
      scrollable
    >
      <Modal.Header closeButton className="bg-light">
        <Modal.Title className="fs-5">
          <div className="d-flex flex-column">
            <span className="fw-bold mb-1">
              Item Code:{" "}
              <span className="text-primary">
                {selectedProduct?.product_code || ""}
              </span>
            </span>
            <span className="fw-bold">
              Product Name:{" "}
              <span className="text-primary">
                {selectedProduct?.product_name || ""}
              </span>
            </span>
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-4">
        <Tabs
          activeKey={activeModule}
          onSelect={(k) => setActiveModule(k)}
          className="mb-3"
          variant="pills"
        >
          <Tab
            eventKey="Sales Invoice"
            title={`Sales Invoice (${getModuleCount("Sales Invoice")})`}
          />
          <Tab
            eventKey="Production"
            title={`Production (${getModuleCount("Production")})`}
          />
          <Tab eventKey="Replenishment History" title="Replenishment History" />
        </Tabs>

        {warehouses.length > 0 ? (
          <Tabs
            activeKey={activeWarehouse}
            onSelect={(k) => setActiveWarehouse(k)}
            className="mb-3"
          >
            {warehouses.map((wh) => (
              <Tab
                key={wh.warehouse_id}
                eventKey={wh.warehouse_id}
                title={
                  wh.warehouse_id === "all"
                    ? "All"
                    : activeModule === "Replenishment History"
                      ? wh.name
                      : `${wh.name} (${getWarehouseCount(activeModule, wh.warehouse_id)})`
                }
              >
                <div className="mt-3">
                  {activeModule === "Replenishment History" ? (
                    <ProductDeficitAccordion pagination={pagination} />
                  ) : (
                    <Table
                      bordered
                      responsive
                      className="text-nowrap align-middle text-center"
                      style={{ tableLayout: "fixed", minWidth: "900px" }}
                    >
                      <thead className="table-light">
                        <tr>
                          <th
                            style={{
                              width:
                                activeModule === "Sales Invoice"
                                  ? "20%"
                                  : "30%",
                            }}
                          >
                            Transaction ID
                          </th>
                          {activeModule === "Sales Invoice" && (
                            <th style={{ width: "15%" }}>DR Number</th>
                          )}
                          <th
                            style={{
                              width:
                                activeModule === "Sales Invoice"
                                  ? "15%"
                                  : "20%",
                            }}
                          >
                            Reserved Stock
                          </th>
                          <th
                            style={{
                              width:
                                activeModule === "Sales Invoice"
                                  ? "15%"
                                  : "20%",
                            }}
                          >
                            Deficit Stock
                          </th>
                          <th style={{ width: "15%" }}>Price (₱)</th>
                          <th
                            style={{
                              width:
                                activeModule === "Sales Invoice"
                                  ? "20%"
                                  : "15%",
                            }}
                          >
                            Date In
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagination.loading ? (
                          <tr>
                            <td colSpan={TABLE_COL_SPAN} className="py-4">
                              <Spinner
                                animation="border"
                                variant="primary"
                                size="sm"
                              />{" "}
                              Getting Details...
                            </td>
                          </tr>
                        ) : pagination.data && pagination.data.length > 0 ? (
                          pagination.data.map((row, idx) => (
                            <tr key={idx}>
                              <td
                                className="fw-semibold text-truncate"
                                title={row.transaction_number}
                              >
                                {row.transaction_number || "N/A"}
                              </td>
                              {activeModule === "Sales Invoice" && (
                                <td
                                  className="text-truncate"
                                  title={row.dr_number}
                                >
                                  {row.dr_number || "N/A"}
                                </td>
                              )}
                              <td className="fw-semibold text-success">
                                {formatToNumber(row.reserved_stock || 0)}
                              </td>
                              <td className="text-danger fw-bold">
                                {formatToNumber(row.stock)}
                              </td>
                              <td>{formatToTwoDecimal(row.price)}</td>
                              <td>{formatDate(row.date_in)}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan={TABLE_COL_SPAN}
                              className="text-muted py-4"
                            >
                              <em>
                                No deficit records found for {wh.name} under{" "}
                                {activeModule}.
                              </em>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  )}

                  {pagination.totalPages > 0 &&
                    activeModule !== "Replenishment History" && (
                      <PaginationControls {...pagination} />
                    )}
                </div>
              </Tab>
            ))}
          </Tabs>
        ) : (
          <div className="text-center py-4">
            <Spinner animation="border" variant="secondary" size="sm" /> Loading
            Warehouses...
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export { ProductDeficitModal };
